import React, { useCallback } from "react";
import { FileError, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, FileCheckIcon, Text  } from '@common-fe/common-fe';

import { ACCEPTED_FILE_TYPES, FileImage, MAX_SIZE_OF_FILE } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import useOnDrop from '@/components/controls/DropScan/useOnDrop';
import useRemoveFileQuery from '@/modules/transaction/queries/useRemoveFile.query';

interface Props {
  isViewMode?: boolean;
  onFileUploadError?: (error?: FileError) => void;
  storeKey?: string;
}

const ChangeFile: React.FC<Props> = ({ isViewMode, onFileUploadError, storeKey }) => {
  const { t } = useTranslation();
  const { values, handleSetValues } = useFilesInDocumentStore(storeKey);
  const { removeFiles } = useRemoveFileQuery();

  const onFilesAdded = useCallback((acceptedFiles: FileImage[]) => {
    // delete old files when selecting new
    if (values?.documentId && values?.files?.length) {
      const fileIds = values?.files?.map((file) => file?.id)?.filter(Boolean);
      removeFiles(fileIds);
    }

    if (acceptedFiles?.length) {
      handleSetValues({ files: acceptedFiles.map(prepareFile) });
    }
  }, [handleSetValues, values?.documentId, values?.files, removeFiles]);

  const { onDrop } = useOnDrop({ onFilesAdded, onFileUploadError, disableFilesUpload: true, storeKey });
  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_OF_FILE,
    onDrop,
    noDrag: true,
    noClick: true,
    multiple: false,
    maxFiles: 1,
  });

  return (
    <Box
      data-testId="change-file"
      pad={{ horizontal: 'spacing24', vertical: 'spacing16' }}
      align="center"
      justify="between"
      direction="row"
      background="canvas"
      round="container2Round"
      border={{ color: 'border2' }}
    >
      <Box direction="row" align="center" data-testId="change-file_name">
        <Box
          round="button2Round"
          border={{ color: 'border2' }}
          pad="spacing4"
          margin={{ right: 'spacing12' }}
          width={{ min: 'auto' }}
        >
          <FileCheckIcon color="iconPrimary" />
        </Box>
        <Text wordBreak="break-all">
          {values?.files?.[0]?.name}
        </Text>
      </Box>

      {isViewMode ? null : (
        <>
          <Anchor
            data-testId="change-file_button"
            size="medium"
            weight={400}
            onClick={open}
          >
            {t('Change file')}
          </Anchor>

          <input {...getInputProps()} />
        </>
      )}
    </Box>
  );
};

export default ChangeFile;
